import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

// Get token from sessionStorage for authentication
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

// TypeScript interfaces for API responses

// 1. All Meals (Admin) - GET /meals-admin
export interface MealItem {
  _id: string;
  name: string;
  price: number;
  image?: string;
  isAvailable: boolean;
  isDiscount: boolean;
  discountAmount?: number;
  createdAt: string;
  updatedAt: string;
  ordersCount: number;
  rating: number;
  totalRatings: number;
  status: "active" | "inactive";
  actions: string;
}

export interface MealsPaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// 2. Meal Details - GET /meals/:id/admin  
export interface MealDetails {
  _id: string;
  name: string;
  description?: string;
  price: number;
  image?: string;
  isAvailable: boolean;
  isDiscount: boolean;
  discountAmount?: number;
  createdAt: string;
  updatedAt: string;
  orderStats: any[];
  ratingStats: any[];
  rating?: number;
  totalRatings?: number;
  category: string;
  status: "active" | "inactive";
  actions: string;
}

// 3. Meals by Restaurant - GET /restaurants/:restaurantId/meals
export interface RestaurantMealItem {
  id: string;
  name: string;
  price: number;
  rating: number;
  orders: number;
  status: "active" | "inactive";
  action: string;
}

// API Response interfaces
interface AllMealsResponse {
  success: boolean;
  data: MealItem[];
  pagination: MealsPaginationInfo;
}

interface MealDetailsResponse {
  success: boolean;
  data: MealDetails;
}

interface RestaurantMealsResponse {
  success: boolean;
  data: RestaurantMealItem[];
}

// Redux state interface
interface MealManagementState {
  // All meals data
  meals: MealItem[];
  mealsStatus: "idle" | "loading" | "success" | "failed";
  mealsError: string | null;
  mealsPagination: MealsPaginationInfo;

  // Current meal details
  currentMealDetails: MealDetails | null;
  detailsStatus: "idle" | "loading" | "success" | "failed";
  detailsError: string | null;

  // Restaurant meals data
  restaurantMeals: RestaurantMealItem[];
  restaurantMealsStatus: "idle" | "loading" | "success" | "failed";
  restaurantMealsError: string | null;
  currentRestaurantId: string | null;
}

const initialState: MealManagementState = {
  // All meals
  meals: [],
  mealsStatus: "idle",
  mealsError: null,
  mealsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },

  // Meal details
  currentMealDetails: null,
  detailsStatus: "idle",
  detailsError: null,

  // Restaurant meals
  restaurantMeals: [],
  restaurantMealsStatus: "idle",
  restaurantMealsError: null,
  currentRestaurantId: null,
};

// Async thunks for API calls

// 1. Get all meals (admin)
export const getAllMeals = createAsyncThunk<
  AllMealsResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "mealManagement/getAllMeals",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<AllMealsResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/meals-admin?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Validate response structure
      if (!response.data || !response.data.data) {
        console.error('Invalid meals API response structure:', response.data);
        return {
          success: false,
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false,
          }
        };
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch meals"
      );
    }
  }
);

// 2. Get meal details
export const getMealDetails = createAsyncThunk<
  MealDetailsResponse,
  string,
  { rejectValue: string }
>(
  "mealManagement/getMealDetails",
  async (mealId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<MealDetailsResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/meals/${mealId}/admin`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch meal details"
      );
    }
  }
);

// 3. Get meals by restaurant
export const getMealsByRestaurant = createAsyncThunk<
  RestaurantMealsResponse,
  string,
  { rejectValue: string }
>(
  "mealManagement/getMealsByRestaurant",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<RestaurantMealsResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/restaurants/${restaurantId}/meals`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurant meals"
      );
    }
  }
);

// Redux slice
const mealManagementSlice = createSlice({
  name: "mealManagement",
  initialState,
  reducers: {
    clearCurrentMealDetails: (state) => {
      state.currentMealDetails = null;
      state.detailsStatus = "idle";
      state.detailsError = null;
    },
    clearRestaurantMeals: (state) => {
      state.restaurantMeals = [];
      state.restaurantMealsStatus = "idle";
      state.restaurantMealsError = null;
      state.currentRestaurantId = null;
    },
    clearErrors: (state) => {
      state.mealsError = null;
      state.detailsError = null;
      state.restaurantMealsError = null;
    },
    setCurrentRestaurantId: (state, action: PayloadAction<string>) => {
      state.currentRestaurantId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all meals
      .addCase(getAllMeals.pending, (state) => {
        state.mealsStatus = "loading";
        state.mealsError = null;
      })
      .addCase(getAllMeals.fulfilled, (state, action) => {
        state.mealsStatus = "success";
        state.meals = action.payload.data;
        state.mealsPagination = action.payload.pagination;
        state.mealsError = null;
      })
      .addCase(getAllMeals.rejected, (state, action) => {
        state.mealsStatus = "failed";
        state.mealsError = action.payload || "Failed to fetch meals";
      })

      // Get meal details
      .addCase(getMealDetails.pending, (state) => {
        state.detailsStatus = "loading";
        state.detailsError = null;
      })
      .addCase(getMealDetails.fulfilled, (state, action) => {
        state.detailsStatus = "success";
        state.currentMealDetails = action.payload.data;
        state.detailsError = null;
      })
      .addCase(getMealDetails.rejected, (state, action) => {
        state.detailsStatus = "failed";
        state.detailsError = action.payload || "Failed to fetch meal details";
      })

      // Get restaurant meals
      .addCase(getMealsByRestaurant.pending, (state) => {
        state.restaurantMealsStatus = "loading";
        state.restaurantMealsError = null;
      })
      .addCase(getMealsByRestaurant.fulfilled, (state, action) => {
        state.restaurantMealsStatus = "success";
        state.restaurantMeals = action.payload.data;
        state.restaurantMealsError = null;
      })
      .addCase(getMealsByRestaurant.rejected, (state, action) => {
        state.restaurantMealsStatus = "failed";
        state.restaurantMealsError = action.payload || "Failed to fetch restaurant meals";
      });
  },
});

export const {
  clearCurrentMealDetails,
  clearRestaurantMeals,
  clearErrors,
  setCurrentRestaurantId,
} = mealManagementSlice.actions;

export default mealManagementSlice.reducer;